import React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import Text from "@/components/ui/text";
import {
  useAccountSettingByKey,
  useUpdateAccountSettingByKey
} from "@/entities/settings/hooks";
import { INTEGRATIONS_TRANSLATION_PREFIX } from "@/pages/settings/components/integrations/integrations";

const ZOOM_PHONE_SETTING_KEY = "integrations.zoom_phone";
const ZOOM_PHONE_PREFIX = `${INTEGRATIONS_TRANSLATION_PREFIX}.items.zoom.phone`;

type ZoomPhoneField = "enabled" | "log_texts";

// The setting stores a JSON-encoded `{"enabled": bool, "log_texts": bool}`; anything else reads as off.
const isOn = (value: unknown, field: ZoomPhoneField): boolean => {
  if (typeof value !== "string") return false;
  try {
    return JSON.parse(value)?.[field] === true;
  } catch {
    return false;
  }
};

// Keeps any other keys stored alongside the changed one, so a toggle never drops them.
const withField = (
  value: unknown,
  field: ZoomPhoneField,
  on: boolean
): string => {
  let existing: Record<string, unknown> = {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        existing = parsed;
      }
    } catch {
      // An unreadable value is replaced outright.
    }
  }
  return JSON.stringify({ ...existing, [field]: on });
};

const CARD_CLASS_NAME =
  "flex w-full items-center justify-between gap-4 rounded-2xl border-[0.5px] border-border-widget-card bg-surface-widget-card p-4";

// Zoom Phone call and text logging runs on the Zoom connection, so it is turned on from the Zoom page.
const ZoomPhoneSetting: React.FC = () => {
  const { t } = useTranslation();
  const setting = useAccountSettingByKey(ZOOM_PHONE_SETTING_KEY);
  const updateSetting = useUpdateAccountSettingByKey();

  if (setting.isError) {
    return (
      <Text variant="smBodyError" data-test="zoom-phone-setting-error">
        {t(`${ZOOM_PHONE_PREFIX}.loadError`)}
      </Text>
    );
  }

  const handleCheckedChange = (field: ZoomPhoneField) => (on: boolean) =>
    updateSetting.mutate(
      {
        pathParams: { settingKey: ZOOM_PHONE_SETTING_KEY },
        data: { value: withField(setting.data?.value, field, on) }
      },
      { onError: () => toast.error(t(`${ZOOM_PHONE_PREFIX}.updateError`)) }
    );

  const busy = setting.isLoading || updateSetting.isPending;
  const callsOn = isOn(setting.data?.value, "enabled");

  return (
    <div className="flex w-full flex-col gap-3">
      <div className={CARD_CLASS_NAME} data-test="zoom-phone-setting">
        <div className="flex flex-col gap-1">
          <Text variant="label">{t(`${ZOOM_PHONE_PREFIX}.label`)}</Text>
          <Text variant="bodySecondary">
            {t(`${ZOOM_PHONE_PREFIX}.description`)}
          </Text>
        </div>
        <Switch
          aria-label={t(`${ZOOM_PHONE_PREFIX}.label`)}
          checked={callsOn}
          onCheckedChange={handleCheckedChange("enabled")}
          disabled={busy}
          data-test="zoom-phone-setting-toggle"
        />
      </div>
      {/* Texts are logged only while call logging is on, so the switch follows it. */}
      <div className={CARD_CLASS_NAME} data-test="zoom-phone-texts-setting">
        <div className="flex flex-col gap-1">
          <Text variant="label">{t(`${ZOOM_PHONE_PREFIX}.textsLabel`)}</Text>
          <Text variant="bodySecondary">
            {t(`${ZOOM_PHONE_PREFIX}.textsDescription`)}
          </Text>
        </div>
        <Switch
          aria-label={t(`${ZOOM_PHONE_PREFIX}.textsLabel`)}
          checked={callsOn && isOn(setting.data?.value, "log_texts")}
          onCheckedChange={handleCheckedChange("log_texts")}
          disabled={busy || !callsOn}
          data-test="zoom-phone-texts-toggle"
        />
      </div>
    </div>
  );
};

export default ZoomPhoneSetting;
